import { useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Card, Badge, Space, Typography, Spin, message, Dropdown, Modal, Input, Alert } from "antd";
import { FolderOutlined, FileOutlined, DownloadOutlined, MoreOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import FileManagerPOA from '../Poa/FileManagerPOA';
import { fetchPoaData } from '../../services/poa/get_poa';
import { downloadFolder } from '../../services/proyectoEstructurado/proyectoEstructuracionTreeService';
import {
  getFolderStats,
  renameFolder,
  deleteFolder,
  FOLDER_NAME_REGEX,
} from '../../services/proyectoEstructurado/proyectoEstructuradoFolderActions';
import { logAction } from '../Poa/logActionsPOA';

const { Text } = Typography;
const NODE_RAIZ = 'DocumentosPOA';

export function FolderCard({
  codigo_tarea,
  filtro,
  item,
  onClick,
  isNavigable = true,
  onUpdateFileCount,
  onFileUpload,
  onRefresh,
  permissions = {},
}) {
  const { canUpdate = false, canDelete = false, canExport = false } = permissions;
  const user = useSelector((state) => state.auth.user);
  const userLogin = user?.username;
  const canNavigate =
    isNavigable &&
    (item.ownFileCount ?? 0) === 0 &&
    item.children &&
    item.children.length > 0;

  const [openModalVisitasEcas, setOpenModalVisitasEcas] = useState(false);
  const [itemParams, setItemParams] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // ── Editar (renombrar) carpeta ────────────────────────────────────────
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);

  // ── Eliminar carpeta ──────────────────────────────────────────────────
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteStats, setDeleteStats] = useState(null);
  const [loadingDeleteStats, setLoadingDeleteStats] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // ✅ useRef en lugar de useState: evita el closure stale en hideModalVisitasEcas.
  // Con useState, useCallback capturaba filesUploaded=false del render anterior.
  // Con useRef, siempre leemos el valor actual en el momento del cierre.
  const filesUploadedRef = useRef(false);

  // Callback que FileManagerPOA llama cada vez que sube/elimina un archivo
  const handleFileUploaded = useCallback(() => {
    filesUploadedRef.current = true;
  }, []);

  const buildRutaFromCodigoTarea = (input) => {
    const codigo = input?.codigo_tarea ?? input;
    if (!codigo) return null;
    const { codActividad, codigo_tarea: codigoTarea } = codigo;
    if (!codActividad || !codigoTarea) return null;
    const actividad = `actividad_${codActividad.replace('.', '')}`;
    const tarea = `tarea_${codigoTarea.replace(/\./g, '')}`;
    return `${actividad}/${tarea}/`;
  };

  const buildRutaFromFiltro = (filtro) => {
    if (!filtro) return null;
    const { actividad, tarea } = filtro;
    if (!actividad || !tarea) return null;
    const actividadRuta = `actividad_${actividad.replace('.', '')}`;
    const tareaRuta = `tarea_${tarea}`;
    return `${actividadRuta}/${tareaRuta}/`;
  };

  const handleClick = () => {
    if (canNavigate) {
      onClick(item);
    } else {
      const banderaCarpetasArchivos = item.dirCount === 0 && item.fileCount === 0;
      let fullRuta = null;
      let codigo_tareaOid = null;

      if (!codigo_tarea) {
        fullRuta = buildRutaFromFiltro(filtro);
        codigo_tareaOid = filtro?.tarea_codigo ?? null;
      } else {
        codigo_tareaOid = codigo_tarea?.codigo_tarea?.codigo_tarea ?? null;
        fullRuta = buildRutaFromCodigoTarea(codigo_tarea);
      }

      const cleanedPath = item.path.replace(fullRuta, '');
      procesarMpoa(codigo_tareaOid, cleanedPath, banderaCarpetasArchivos);
    }
  };

  const handleDownload = async (e) => {
    if (e) e.stopPropagation();
    if (item.type !== "folder") return;

    setDownloading(true);
    try {
      const response = await downloadFolder(item.path);
      const blob = new Blob([response], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${item.name}.zip`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      message.success(`Descarga de "${item.name}" iniciada`);
    } catch (error) {
      console.error('Error al descargar la carpeta:', error);
      message.error('Error al descargar la carpeta');
    } finally {
      setDownloading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────
  // EDITAR (RENOMBRAR) CARPETA
  // ─────────────────────────────────────────────────────────────────────
  const handleOpenRenameModal = (e) => {
    if (e) e.stopPropagation();
    setRenameValue(item.viewName || item.name);
    setRenameModalOpen(true);
  };

  const handleRenameValueChange = (e) => {
    const value = e.target.value.normalize('NFC');
    if (FOLDER_NAME_REGEX.test(value) || value === '') {
      setRenameValue(value);
    } else {
      message.error('El nombre contiene caracteres no permitidos.');
    }
  };

  const handleRenameConfirm = async () => {
    if (!renameValue.trim()) {
      message.warning('Debes ingresar un nombre para la carpeta.');
      return;
    }
    setRenaming(true);
    try {
      const lastSlash = item.path.lastIndexOf('/');
      const parentPath = lastSlash >= 0 ? item.path.slice(0, lastSlash) : '';
      const newRelativePath = item.path.replace(/[^/]+$/, renameValue);
      const fullPath = `${NODE_RAIZ}/${newRelativePath}`.replace(/\/{2,}/g, '/');
      const resourceInfo = logAction(userLogin, 'updateFolder', renameValue, {
        nameResource: renameValue,
        Path: newRelativePath,
        fatherResource: `${NODE_RAIZ}/${parentPath}`.replace(/\/{2,}/g, '/'),
        typeResource: 'folder',
        key: fullPath,
        fullPath,
        resourceType: 'Folder ProyectoEstructurado',
      });
      const res = await renameFolder(item.path, renameValue, resourceInfo);
      if (res.status === 200) {
        const affected = res.data?.affectedNestedRows ?? 0;
        message.success(
          affected > 0
            ? `Carpeta renombrada correctamente. Se actualizó la ruta de ${affected} archivo(s) asociados.`
            : 'Carpeta renombrada correctamente.'
        );
        setRenameModalOpen(false);
        if (onRefresh) onRefresh(null, null);
      } else {
        message.error('Error al renombrar la carpeta.');
      }
    } catch (error) {
      console.error('Error al renombrar la carpeta:', error);
      message.error('Error al renombrar la carpeta.');
    } finally {
      setRenaming(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────
  // ELIMINAR CARPETA
  // ─────────────────────────────────────────────────────────────────────
  const handleOpenDeleteConfirm = async (e) => {
    if (e?.domEvent) e.domEvent.stopPropagation();
    setDeleteModalOpen(true);
    setDeleteStats(null);
    setLoadingDeleteStats(true);
    try {
      const stats = await getFolderStats(item.path);
      setDeleteStats(stats);
    } catch (error) {
      console.error('Error al obtener las estadísticas de la carpeta:', error);
      message.error('No se pudo calcular el contenido de la carpeta.');
      setDeleteModalOpen(false);
    } finally {
      setLoadingDeleteStats(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      const fullPath = `${NODE_RAIZ}/${item.path}`.replace(/\/{2,}/g, '/');
      const resourceInfo = logAction(userLogin, 'deleteFolder', item.viewName || item.name, {
        nameResource: item.viewName || item.name,
        Path: item.path,
        fatherResource: `${NODE_RAIZ}/${item.path}`.replace(/\/{2,}/g, '/'),
        typeResource: 'folder',
        key: item.path,
        fullPath,
        resourceType: 'Folder ProyectoEstructurado',
      });
      const res = await deleteFolder(item.path, resourceInfo);
      if (res.status === 200) {
        message.success('Carpeta eliminada correctamente.');
        setDeleteModalOpen(false);
        if (onRefresh) onRefresh(null, null);
      } else {
        message.error('Error al eliminar la carpeta.');
      }
    } catch (error) {
      console.error('Error al eliminar la carpeta:', error);
      message.error('Error al eliminar la carpeta.');
    } finally {
      setDeleting(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────
  // Al cerrar FileManagerPOA:
  //  - Si hubo subidas/eliminaciones → llama onRefresh (que en
  //    FolderNavigator invalida cache y hace refresh completo)
  //  - Resetea el flag filesUploaded
  // ─────────────────────────────────────────────────────────────────────
  const hideModalVisitasEcas = useCallback(() => {
    setOpenModalVisitasEcas(false);
    setItemParams(null);

    // ✅ Lee el ref directamente — siempre el valor actual, nunca stale
    if (filesUploadedRef.current) {
      filesUploadedRef.current = false;
      if (onRefresh) {
        onRefresh(null, item.path);
      }
    }
  // item.path y onRefresh son estables; no necesitamos filesUploaded en deps
  }, [onRefresh, item.path]);

  const procesarMpoa = async (Mpoa, ruta, banderaCarpetasArchivos) => {
  if (!Mpoa) {
      setOpenModalVisitasEcas(false);
      setItemParams(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    const manejarActividadEspecial = (actividad) => {
      switch (actividad) {
        case '2.2.1.1':
        case '2.2.1.2':
        case '2.2.1.3':
          return '2.2.1';
        default:
          return actividad;
      }
    };

    const actividadBase = manejarActividadEspecial(Mpoa);

    try {
      const dataCompleta = await fetchPoaData();
      const dataPoa = dataCompleta.dataPoa;

      const findNodePath = (node, path = []) => {
        path.push(node);
        if (node.codigo_tarea === actividadBase && !['2.2.1.2', '2.2.1.3'].includes(Mpoa)) {
          return path;
        }
        if (['2.2.1.2', '2.2.1.3'].includes(Mpoa)) {
          if (node.children) {
            for (let child of node.children) {
              if (Mpoa === '2.2.1.2' && child.key === 'indicador_33') return [...path, child];
              if (Mpoa === '2.2.1.3' && child.key === 'indicador_34') return [...path, child];
            }
          }
        }
        if (node.children) {
          for (let child of node.children) {
            const result = findNodePath(child, [...path]);
            if (result) return result;
          }
        }
        return null;
      };

      let matchedPath = null;
      for (let nodo of dataPoa) {
        matchedPath = findNodePath(nodo);
        if (matchedPath) break;
      }

      if (matchedPath) {
        const initialData = extractData(matchedPath);
        const actividadData = initialData[0].actividadData;
        // El primer segmento de la ruta es el key del indicador al que pertenece
        // la carpeta clickeada (ej: "indicador_34/medio_verificacion_29/...").
        // Como un mismo codigo_tarea puede tener varios indicadores asociados,
        // usamos ese key para encontrar el indicador correcto.
        const indicadorKeyFromRuta = ruta?.split('/')[0];
        const tareaEncontrada = actividadData.find((item) => {
          if (['2.2.1.2', '2.2.1.3'].includes(Mpoa)) {
            return (
              item.codigo_tarea === actividadBase &&
              ((Mpoa === '2.2.1.2' && item.key === 'indicador_33') ||
                (Mpoa === '2.2.1.3' && item.key === 'indicador_34'))
            );
          }
          if (indicadorKeyFromRuta) {
            return item.codigo_tarea === actividadBase && item.key === indicadorKeyFromRuta;
          }
          return item.codigo_tarea === actividadBase;
        });
        if (tareaEncontrada) {
          tareaEncontrada.rutaEstructuracion = ruta;
          tareaEncontrada.banderaCarpetasArchivos = banderaCarpetasArchivos;
          setItemParams(tareaEncontrada);
          setOpenModalVisitasEcas(true);
        } else {
          setOpenModalVisitasEcas(false);
          setItemParams(null);
        }
      } else {
        console.warn("No se encontró ningún nodo coincidente");
      }
    } catch (error) {
      console.error("Error al obtener los datos:", error);
    } finally {
      setLoading(false);
    }
  };

  const extractData = (children) => {
    return children.flatMap(child => {
      if (child.children && child.tipo === 'Actividad') {
        const actividadData = child.children.flatMap(grandchild => {
          if (grandchild.children && grandchild.tipo === 'Tarea') {
            return grandchild.children.flatMap(greatGrandchild => {
              if (greatGrandchild.tipo === 'Indicador') {
                const fecha = greatGrandchild.meta_e?.[0]?.fecha;
                const fechaFormateada = fecha
                  ? new Date(fecha).toLocaleDateString('es-ES')
                  : '-';
                const medioV = greatGrandchild.medioVerificacion || null;
                return {
                  key: greatGrandchild.key,
                  codigo_tarea: grandchild.codigo_tarea,
                  codActividad: grandchild.codActividad,
                  nameActividad: child.actividad,
                  tarea: grandchild.tarea,
                  indicador: greatGrandchild.title,
                  medioV,
                  fechas: fechaFormateada,
                  responsables: 'N/A',
                  soportes: 'N/A',
                  datoIndicador: greatGrandchild,
                };
              }
              return [];
            });
          }
          return [];
        });
        return [{ actividadTitle: child.title, actividad: child.actividad, actividadData }];
      }
      return [];
    });
  };

  return (
    <>
      <Card
        onClick={handleClick}
        style={{
          height: 110,
          padding: 16,
          cursor: canNavigate ? "pointer" : "default",
          display: "flex",
          flexDirection: "column",
          borderColor: canNavigate ? "#43a1f8ff" : "#f0f0f0",
          position: 'relative',
        }}
        styles={{ body: { padding: 0 } }}
      >
        {item.type === "folder" && (() => {
          const dropdownItems = [
            ...(canExport ? [{
              key: "download",
              icon: <DownloadOutlined />,
              label: 'Descargar carpeta',
              onClick: (e) => {
                e.domEvent.stopPropagation();
                handleDownload(e.domEvent);
              },
            }] : []),
            ...(canUpdate ? [{
              key: "rename",
              icon: <EditOutlined />,
              label: 'Editar carpeta',
              onClick: (e) => {
                e.domEvent.stopPropagation();
                handleOpenRenameModal(e.domEvent);
              },
            }] : []),
            ...(canDelete ? [{
              key: "delete",
              icon: <DeleteOutlined />,
              danger: true,
              label: 'Eliminar carpeta',
              onClick: (e) => {
                e.domEvent.stopPropagation();
                handleOpenDeleteConfirm(e);
              },
            }] : []),
          ];

          if (dropdownItems.length === 0) return null;

          return (
          <Dropdown
            menu={{ items: dropdownItems }}
            placement="bottomRight"
            trigger={['click']}
          >
            <span
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px',
                zIndex: 1,
                color: (item.dirCount === 0 && item.fileCount === 0) ? '#ccc' : '#1890ff',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreOutlined style={{ fontSize: '16px' }} />
            </span>
          </Dropdown>
          );
        })()}

        <Space align="start" style={{ width: "100%" }}>
          {item.type === "folder" ? (
            <FolderOutlined style={{ fontSize: 32 }} />
          ) : (
            <FileOutlined style={{ fontSize: 32, color: "#8c8c8c" }} />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text
              strong
              style={{
                whiteSpace: "normal",
                wordWrap: "break-word",
                wordBreak: "break-word",
                overflowWrap: "break-word",
                display: "block",
                maxWidth: "95%",
              }}
            >
              {/* viewName ya trae el prefijo visual cuando aplica */}
              {item.viewName || item.name}
            </Text>

            <Space style={{ paddingTop: 10, paddingRight: 5 }}>
              <Badge
                count={`${item.dirCount.toLocaleString()} carpetas`}
                style={{ backgroundColor: "#fac840ff", color: "#fff" }}
              />
            </Space>

            <Space style={{ paddingTop: 10, paddingLeft: 5 }}>
              <Badge
                count={`${item.fileCount.toLocaleString()} archivos`}
                style={{
                  backgroundColor: "#52c41a",
                  color: "#fff",
                  opacity: item.fileCount === 0 ? 0.5 : 1,
                }}
              />
            </Space>

            {item.typeS && (
              <Badge
                count={item.typeS}
                style={{ backgroundColor: "#fff", border: "1px solid #d9d9d9", color: "#000" }}
              />
            )}
          </div>
        </Space>
      </Card>

      {/* Modal de descarga con spinner */}
      <Modal
        open={downloading}
        footer={null}
        closable={false}
        destroyOnHidden
        centered
        width={300}
        style={{ textAlign: 'center' }}
      >
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Descargando carpeta...</div>
      </Modal>

      {/* Modal: Editar (renombrar) carpeta */}
      <Modal
        title="Editar nombre de la carpeta"
        open={renameModalOpen}
        onOk={handleRenameConfirm}
        confirmLoading={renaming}
        onCancel={() => setRenameModalOpen(false)}
        okText="Guardar"
        cancelText="Cancelar"
      >
        {item.fileCount > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={`Esta carpeta contiene ${item.fileCount.toLocaleString()} archivo(s) en su interior. Al renombrarla se actualizará la ruta registrada de esos archivos para no perder la trazabilidad.`}
          />
        )}
        <Input
          placeholder="Nuevo nombre de la carpeta"
          value={renameValue}
          onChange={handleRenameValueChange}
          onPressEnter={handleRenameConfirm}
        />
      </Modal>

      {/* Modal: Eliminar carpeta */}
      <Modal
        title="Eliminar carpeta"
        open={deleteModalOpen}
        onOk={handleDeleteConfirm}
        confirmLoading={deleting}
        onCancel={() => setDeleteModalOpen(false)}
        okText="Eliminar"
        okButtonProps={{ danger: true, disabled: loadingDeleteStats }}
        cancelText="Cancelar"
      >
        {loadingDeleteStats ? (
          <div style={{ textAlign: 'center', padding: 16 }}>
            <Spin />
            <div style={{ marginTop: 8 }}>Calculando el contenido de la carpeta...</div>
          </div>
        ) : (
          <Alert
            type="error"
            showIcon
            message={
              `Esta acción eliminará permanentemente ${deleteStats?.folderCount ?? 0} carpeta(s) y ` +
              `${deleteStats?.fileCount ?? 0} archivo(s) contenidos en "${item.viewName || item.name}". ` +
              'Esta acción no se puede deshacer.'
            }
          />
        )}
      </Modal>

      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0, left: 0,
            width: "100%", height: "100%",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            display: "flex", justifyContent: "center", alignItems: "center",
            zIndex: 9999,
          }}
        >
          <Spin tip="Cargando..." size="large" />
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────────
          FileManagerPOA recibe onUploaded para notificar cada vez que
          sube o elimina un archivo, sin necesidad de tocar FileManagerPOA
          internamente. La señal se recoge en handleFileUploaded.
      ───────────────────────────────────────────────────────────────── */}
      {openModalVisitasEcas && !loading && (
        <FileManagerPOA
          codigo_tarea={{ codigo_tarea: itemParams }}
          onClose={hideModalVisitasEcas}
          onUploaded={handleFileUploaded}
        />
      )}
    </>
  );
}